/**
 * RightDrawer — shared right-side drawer primitive (catalogue v3,
 * June 10 2026 redesign).
 *
 * The 2026-06 wireframes lean on a recurring surface: a full-height
 * panel pinned to the right edge of the viewport, over a faint backdrop,
 * holding either the Contributors directory (page 5) or the Display
 * settings cluster (page 2). Both used to be hand-rolled — the
 * ContributorsDirectory shipped its own backdrop + Esc handler + focus
 * juggling. This primitive consolidates that chrome so every drawer
 * behaves identically (Esc to close, backdrop click to dismiss, focus
 * lands on the close button and is restored to the trigger on close)
 * and so a third drawer is a five-line component, not a copy-paste of
 * the focus plumbing (engineering-review decision R1).
 *
 * It is intentionally NON-modal (aria-modal="false"): the map under the
 * drawer stays legible and interactive-ish, matching the wireframe
 * intent of a side panel rather than a focus-trapping dialog. We do not
 * trap Tab for the same reason.
 *
 * Composition:
 *   - `title`      → the <h2> in the header.
 *   - `subHeader`  → optional non-scrolling content under the title row
 *                    (e.g. the directory's search box + result count).
 *   - children     → the scrollable body.
 *   - `footer`     → optional non-scrolling footer band.
 *
 * Stacking: `zIndex` lets a drawer opened FROM another drawer (the
 * Contributors directory launched from the Display settings drawer) sit
 * above its parent. The backdrop renders one below the panel.
 *
 * NOTE: pass a STABLE `onClose` (useCallback) — the Esc listener depends
 * on it. The focus-restore effect deliberately depends only on `open`
 * so a parent re-render can't yank focus mid-session.
 */

import React, { useEffect, useRef } from "react";
import "./RightDrawer.css";

export default function RightDrawer({
  open,
  onClose,
  title,
  ariaLabel,
  subHeader = null,
  footer = null,
  children,
  width = 360,
  zIndex = 1900,
  className = "",
  closeLabel = "Close",
}) {
  const closeBtnRef = useRef(null);
  // The element focused before the drawer opened, so we can hand focus
  // back when it closes (keyboard users don't get dumped at <body>).
  const restoreFocusRef = useRef(null);

  // Focus management — depends ONLY on `open` so identity churn in
  // `onClose` can't trigger a spurious focus restore while open.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current =
      typeof document !== "undefined" ? document.activeElement : null;
    const raf = window.requestAnimationFrame(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    });
    return () => {
      window.cancelAnimationFrame(raf);
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, [open]);

  // Esc closes. Attached to document so it works regardless of which
  // child holds focus; stopPropagation so a parent surface (e.g. an
  // open MUI menu) doesn't also react to the same keystroke.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="right-drawer__backdrop"
        onClick={onClose}
        aria-hidden="true"
        style={{ zIndex: zIndex - 1 }}
      />
      <aside
        className={["right-drawer", className].filter(Boolean).join(" ")}
        role="dialog"
        aria-modal="false"
        aria-label={ariaLabel || title}
        style={{ width, maxWidth: "92vw", zIndex }}
      >
        <header className="right-drawer__header">
          <div className="right-drawer__title-row">
            <h2 className="right-drawer__title">{title}</h2>
            <button
              ref={closeBtnRef}
              type="button"
              className="right-drawer__close"
              onClick={onClose}
              aria-label={closeLabel}
            >
              ×
            </button>
          </div>
          {subHeader}
        </header>

        <div className="right-drawer__body">{children}</div>

        {footer ? <footer className="right-drawer__footer">{footer}</footer> : null}
      </aside>
    </>
  );
}
